import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { switchMap, takeWhile } from 'rxjs/operators';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { SaveComponent } from '@components/admin/dialog/save/save.component';
import { ReturnOrderDialogComponent, ReturnOrderDialogResult } from '@components/admin/patient/return-order-dialog/return-order-dialog.component';
import { EmailOrderDialogComponent } from '@components/admin/patient/email-order-dialog/email-order-dialog.component';
import { DeleteOrderDialogComponent, DeleteOrderDialogResult } from '@components/admin/patient/delete-order-dialog/delete-order-dialog.component';
import { DeleteComponent } from '@components/admin/dialog/delete/delete.component';
import { OkCancelComponent } from '@components/admin/dialog/ok-cancel/ok-cancel.component';

import { FormatData } from '@classes/format-data';
import { OrderDocument } from '@classes/order-document';
import { ExchangeJob } from '@classes/exchange-job';
import { ReturnJob } from '@classes/return-job';
import { PatientForm } from '@classes/patient-form';
import { OrderForm } from '@classes/order-form';

import { Patient } from '@models/patient';
import { PatientService } from '@services/patient.service';

import { Order } from '@models/order';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';

import { Prescription } from '@models/prescription';
import { PrescriptionService } from '@services/prescription.service';
import { AuthService } from '@services/auth.service';
import { Store } from '@models/store';
import { StoreService } from '@services/store.service';

import { FileUploader } from 'ng2-file-upload';
import { environment } from '@environments/environment';

import { parsePhoneNumber } from 'libphonenumber-js';

import * as ClassicEditorBuild from '@ckeditor/ckeditor5-build-classic';
import { Subscription } from 'rxjs';
import { EyeExamService } from '@services/eye-exam.service';
import { pick, omit } from 'lodash';
import moment from 'moment';
import { NotificationService } from '@services/notification.service';
import { PatientDialogComponent } from '../dialog/patient/patient-dialog.component';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';
import { PatientOrdersComponent } from '@components/admin/patient/patient-orders/patient-orders.component';

@Component({
  selector: 'app-patient',
  templateUrl: './patient.component.html',
  styleUrls: ['./patient.component.css'],
})
export class PatientComponent implements OnInit {
  @Output() clickEvent: EventEmitter<any> = new EventEmitter();
  @ViewChild(PatientOrdersComponent) patientOrdersList: PatientOrdersComponent;
  patient: Patient;
  patientId: string;
  newPatient: boolean = false;
  newPatientName: string;
  patientForm: FormGroup;
  patientDob: string;
  saveButtonClicked: Boolean = false;
  loadingOrders: Boolean = true;
  loadingPrescriptions: Boolean = true;

  public orderImages: String[] = [];
  public orders: Order[] = [];
  public prescriptions: Prescription[] = [];
  public prescriptionImages: String[] = [];
  public patientOrders: Order[] = [];
  public uploaderOrder: FileUploader = new FileUploader({
    url: environment.base_url + '/image/order',
    itemAlias: 'photo',
  });
  public uploaderPrescription: FileUploader = new FileUploader({
    url: environment.base_url + '/image/prescription',
    itemAlias: 'photo',
  });
  orderForms: FormArray[] = [];
  storeOptions: any[];
  authorizedRoles: string[] = ['MANAGER', 'OWNER/ADMIN'];
  authorized: boolean = false;
  loading: boolean = true;
  currentStore: Store;
  storeSubscription: Subscription;
  panelOpenStatePatientInformation: boolean = true;
  panelOpenStateInquiries: boolean = false;
  panelOpenStateOrders: boolean = false;
  panelOpenStateDoctorInformation: boolean = false;
  panelOpenStatePrescriptions: boolean = false;
  panelOpenStateScannedOrders: boolean = false;
  ordersPanelLoaded: boolean = false;
  prescriptionsPanelLoaded: boolean = false;
  scannedOrdersFetched: boolean = false;
  scannedOrdersLoading: boolean = false;
  scannedOrders: Order[] = [];
  private orderDialogRef: MatDialogRef<OrderEditDialogComponent> | null = null;
  private orderDialogOrderId: string | null = null;

  public ClassicEditorBuild = ClassicEditorBuild;
  canCopy: boolean = false;
  discounts = [
    { value: 'EMPLOYEE', viewValue: 'EMPLOYEE' },
    { value: 'FRIENDSFAMILY', viewValue: 'FRIENDS & FAMILY' },
    { value: 'SENIOR', viewValue: 'SENIOR' },
  ];

  canDeactivate() {
    if (this.patientForm.dirty && !this.saveButtonClicked) {
      return window.confirm('Discard changes?');
    }
    return true;
  }

  constructor(
    private patientService: PatientService,
    private orderService: OrderService,
    private productService: ProductService,
    private prescriptionService: PrescriptionService,
    public storeService: StoreService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private auth: AuthService,
    private notificationService: NotificationService,
    private cdRef: ChangeDetectorRef,
    private eyeExamService: EyeExamService
  ) {
    this.storeOptions = this.storeService.stores;
    this.patientForm = this.fb.group({});

    this.storeSubscription = this.auth
      .getStoreObjectMessage()
      .subscribe((store: Store) => {
        this.currentStore = store;
      });

    this.route.paramMap.subscribe((params: ParamMap) => {
      this.patientId = params.get('id');
      this.newPatient = this.router.url.includes('/admin/patient/new');
      this.newPatientName = params.get('name');
      if (!this.newPatient && this.patientId) {
        this.getPatient();
      } else if (this.newPatient || !this.patientId) {
        this.patient = new Patient();
        this.newPatient = true;
        this.setPatientForm();
        this.setNewPatientName(this.newPatientName);
      }
      this.syncOrderDialog(this.route.snapshot.queryParamMap.get('order'));
    });

    this.route.queryParamMap.subscribe((params: ParamMap) => {
      this.syncOrderDialog(params.get('order'));
    });
  }

  ngAfterViewInit() {
    this.authorized = this.authorizedRoles.includes(this.auth.currentUser.role);

    const { exam } = this.route.snapshot.queryParams;
    if (this.newPatient && exam) {
      this.fillPatientWithExam(exam);
    }

    this.cdRef.detectChanges();
  }

  getPatient() {
    this.patientService.getPatientSummary(this.patientId).subscribe((patient) => {
      this.patient = patient;
      this.setPatientForm();
      if (patient.alertNotes || patient.dns) {
        this.showPatientAlert();
      }
    });
  }

  onOrdersPanelOpened() {
    this.panelOpenStateOrders = true;
    this.ordersPanelLoaded = true;
  }

  private syncOrderDialog(orderId: string | null): void {
    const patientId = this.patientId || this.route.snapshot.paramMap.get('id');
    if (!orderId || !patientId || this.newPatient) {
      if (!orderId && this.orderDialogRef) {
        this.orderDialogRef.close();
      }
      return;
    }
    if (this.orderDialogRef && this.orderDialogOrderId === orderId) {
      return;
    }
    if (this.orderDialogRef) {
      const previous = this.orderDialogRef;
      this.orderDialogRef = null;
      this.orderDialogOrderId = null;
      previous.close();
    }
    this.openOrderDialog(patientId, orderId);
  }

  private openOrderDialog(patientId: string, orderId: string): void {
    this.orderDialogOrderId = orderId;
    this.orderDialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      closeOnNavigation: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });
    this.orderDialogRef.afterClosed().subscribe((saved) => {
      const closedId = orderId;
      if (this.orderDialogOrderId === closedId) {
        this.orderDialogRef = null;
        this.orderDialogOrderId = null;
        if (this.route.snapshot.queryParamMap.get('order') === closedId) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { order: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      }
      if (saved) {
        this.patientOrdersList?.getNewOrders();
      }
    });
  }

  onPrescriptionsPanelOpened() {
    this.panelOpenStatePrescriptions = true;
    this.prescriptionsPanelLoaded = true;
  }

  onScannedOrdersPanelOpened() {
    this.panelOpenStateScannedOrders = true;
    if (!this.scannedOrdersFetched) {
      this.scannedOrdersLoading = true;
      this.orderService
        .getPatientOrders(this.patientId, 'orders')
        .subscribe((orders: Order[]) => {
          this.scannedOrders = orders;
          this.scannedOrdersLoading = false;
          this.scannedOrdersFetched = true;
          this.cdRef.detectChanges();
        });
    }
  }

  setPatientForm() {
    this.patientForm = PatientForm.initPatientFormGroup(
      this.patient,
      this.auth,
      this.storeOptions
    );
    this.loading = false;
  }

  blurDate() {
    this.patientForm.get('dob').setValue(this.patientForm.get('dob').value);
  }

  blurDate2(prescriptionForm) {
    prescriptionForm.setValue(prescriptionForm.value);
  }

  ngOnInit(): void {
    this.canCopy = /admin/gi.test(this.auth.currentUser.role);
    if (history.state?.orderSaveMessage || history.state?.orderAdded) {
      this.notificationService.show(
        'success',
        history.state.orderSaveMessage || 'Successfully added order',
        'OK'
      );
      const { orderAdded, orderSaveMessage, ...rest } = history.state || {};
      history.replaceState(rest, '');
    }
  }

  getPrefix(sex: string): string {
    let prefix;
    switch (sex) {
      case 'Male':
        prefix = 'MR';
        break;
      case 'Female':
        prefix = 'MS';
        break;
      default:
        break;
    }
    return prefix;
  }

  fillPatientWithExam(examId: string) {
    this.eyeExamService.findById(examId).subscribe((exam) => {
      this.patient = {
        ...this.patient,
        store: this.auth.getStoreObject()._id,
        ...pick(exam, [
          'internalPatientId',
          'firstName',
          'lastName',
          'address',
          'city',
          'state',
          'zip',
          'email',
          'occupation',
          'dob',
          'phone',
          'cell',
        ]),
        lastExam: exam.lastExam
          ? moment.utc(exam.lastExam).format('YYYY-MM-DD')
          : null,
        prefix: this.getPrefix(exam.sex),
        prescriptions: exam.prescriptions,
        from2020Now: true,
      };
      this.setPatientForm();
    });
  }

  prepareSavePatient(): Patient {
    const formModel = this.patientForm.value;
    const date = new Date(formModel.dob);
    const patientDob =
      formModel.dob != null &&
      formModel.dob !== '' &&
      date instanceof Date &&
      !isNaN(date.valueOf())
        ? date.toString()
        : null;
    const orderIds = (this.patient.orders || []).map((a: any) =>
      typeof a === 'string' ? a : a._id || ''
    );
    const prescriptionIds = (this.patient.prescriptions || []).map((a: any) =>
      typeof a === 'string' ? a : a._id || ''
    );

    const savePatient: Patient = {
      _id: this.patient._id as string,
      internalPatientId: this.patient.internalPatientId,
      prefix: formModel.prefix as string,
      patientNumber: formModel.patientNumber as string,
      store: formModel.store as string,
      dob: patientDob as string,
      dateAdded: new Date().toString(),
      dateUpdated: new Date().toString(),
      discountStatus: formModel.discountStatus as string,
      storeCredit: FormatData.formatFloat(formModel.storeCredit) as number,
      firstName: formModel.firstName as string,
      lastName: formModel.lastName as string,
      address: formModel.address as string,
      address2: formModel.address2 as string,
      city: formModel.city as string,
      state: formModel.state as string,
      zip: formModel.zip as string,
      phone: formModel.phone as string,
      phone2: formModel.phone2 as string,
      cell: formModel.cell as string,
      email: formModel.email as string,
      since: formModel.since as string,
      badAddress: formModel.badAddress as string,
      occupation: formModel.occupation as string,
      interests: formModel.interests as string,
      favoriteLines: formModel.favoriteLines as string[],
      familyMembers: formModel.familyMembers as string,
      beBackList: formModel.beBackList as string,
      howDidYouFindUs: formModel.howDidYouFindUs as string,
      howDidYouFindUsOther: formModel.howDidYouFindUsOther as string,
      patientNotes: formModel.patientNotes as string,
      sleep: !!formModel.sleep as boolean,
      optOut: !!formModel.optOut as boolean,
      dns: !!formModel.dns as boolean,
      _doctorId: '',
      doctorName: formModel.doctorName as string,
      doctorPhone: formModel.doctorPhone as string,
      doctorFax: formModel.doctorFax as string,
      doctorEmail: formModel.doctorEmail as string,
      lastExam: formModel.lastExam as string,
      callForRx: !!formModel.callForRx as boolean,
      //prescriptions: '',
      prescriptionNotes: formModel.prescriptionNotes as string,
      //prescriptionImageIDs: '',
      //images: this.patient.images,
      //images2: this.patient.images2,
      //imageData: []
      orders: orderIds,
      prescriptions:
        this.patient.prescriptions.length > 0 && !prescriptionIds.join('')
          ? this.patient.prescriptions
          : prescriptionIds,
      from2020Now: formModel.from2020Now,
      alertNotes: !!formModel.alertNotes,
    };
    return savePatient;
  }

  prepareSaveOrders() {
    for (
      let x = 0;
      x < this.patientForm.controls.orderForms['controls'].length;
      x++
    ) {
      const saveOrder =
        this.patientForm.controls.orderForms['controls'][x].value;
      const order = new Order();
      order._id = saveOrder._id;
      order.invoiceNumber = saveOrder.invoiceNumber;
      order.type = saveOrder.type;
      order.archived = saveOrder.archived;
      order.patient = this.patient['_id'];
      this.route.paramMap
        .pipe(
          switchMap((params: ParamMap) =>
            this.orderService.updateOrder(saveOrder._id, order)
          )
        )
        .subscribe();
    }
  }

  prepareSavePrescriptions() {
    for (
      let x = 0;
      x < this.patientForm.controls.prescriptionForms['controls'].length;
      x++
    ) {
      const savePrescription: Prescription = this.patientForm.controls
        .prescriptionForms['controls'][x].value as Prescription;
      const date = new Date(savePrescription.expirationDate);
      savePrescription.expirationDate =
        savePrescription.expirationDate != null &&
        savePrescription.expirationDate != '' &&
        date instanceof Date &&
        !isNaN(date.valueOf())
          ? date.toString()
          : null;
      savePrescription.patientId = this.patient['_id'];

      this.route.paramMap
        .pipe(
          switchMap((params: ParamMap) =>
            this.prescriptionService.updatePrescription(
              savePrescription._id,
              savePrescription
            )
          )
        )
        .subscribe();
    }
  }

  save(): void {
    if (!this.saveButtonClicked) {
      this.patient = this.prepareSavePatient();
      // this.prepareSaveOrders();
      // this.prepareSavePrescriptions();
      this.saveButtonClicked = true;

      if (this.newPatient) {
        this.patientService
          .addPatient(omit(this.patient, ['_id']))
          .subscribe((res) => {
            this.notificationService.show('success', 'PATIENT SAVED', 'OK');
            this.patient._id = res['patient']['_id'];
            this.router.navigate(['/admin/patient', this.patient._id]);
          });
      } else {
        this.patientService.updatePatient(this.patient).subscribe((patient) => {
          this.notificationService.show('success', 'PATIENT SAVED', 'OK');
          this.router.navigateByUrl('/admin');
        });
      }
    }
  }

  copyPatient(): void {
    const dialogRef = this.dialog.open(PatientDialogComponent, {
      width: '800px',
      data: { patients: [this.patient], message: 'COPY PATIENT?' },
    });
    dialogRef.afterClosed().subscribe((patient?: Patient) => {
      if (patient) {
        this.router
          .navigate(['admin', 'patient', patient._id])
          .then(() => location.reload());
      }
    });
  }

  deletePatient(): void {
    const dialogRef = this.dialog.open(DeleteComponent, {
      width: '250px',
      data: { message: 'DELETE PATIENT?' },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result == true) {
        this.patientService
          .deletePatient(this.patient._id)
          .subscribe((patient) => {
            this.notificationService.show('success', 'PATIENT DELETED', 'OK');
            this.router.navigateByUrl('/admin');
          });
      }
    });
  }

  setOrderAsPrescription(inputObj): void {
    const orderId = inputObj._id;
    const index = inputObj.index;
    const dialogRef = this.dialog.open(OkCancelComponent, {
      width: '250px',
      data: { message: 'SET ORDER AS PRESCRIPTION?' },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe(() => {
        this.orderService
          .setOrderAsPrescription(orderId)
          .subscribe((prescription) => {
            let orderFormArray: FormArray;
            orderFormArray = this.patientForm.controls.orderForms as FormArray;
            orderFormArray.removeAt(index);
            const newPrescriptionFormGroup = OrderForm.initOrderFormGroup(
              prescription,
              this.auth,
              this.storeOptions
            );
            const patientPrescriptionsFormArray: FormArray =
              this.patientForm.get('prescriptions') as FormArray;
            patientPrescriptionsFormArray.controls.unshift(
              newPrescriptionFormGroup
            );
            this.notificationService.show(
              'success',
              'ORDER CHANGED TO PRESCRIPTION',
              'OK'
            );
          });
      });
  }

  deleteOrder(inputObj): void {
    if (this.auth?.currentUser?.role !== 'OWNER/ADMIN') {
      this.notificationService.show(
        'error',
        'DELETE is limited to administrators',
        'OK'
      );
      return;
    }
    const orderId = inputObj._id;
    const dialogRef = this.dialog.open(DeleteOrderDialogComponent, {
      width: '420px',
      data: { invoiceNumber: inputObj.order?.invoiceNumber || inputObj.invoiceNumber },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result?.reason))
      .subscribe((result: DeleteOrderDialogResult) => {
        this.orderService
          .deleteOrder(orderId, {
            reason: result.reason,
            notes: result.notes,
            initials: String(this.auth.currentUser?.initials || '').trim(),
          })
          .subscribe((order) => {
            this.openDialog('ORDER DELETED');
            this.notificationService.show('success', 'ORDER DELETED', 'OK');
          });
      });
  }

  emailOrder(inputObj): void {
    const order = inputObj.order || inputObj;
    const invoiceNumber = String(order?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(EmailOrderDialogComponent, {
      width: '420px',
      data: {
        email: this.patientForm?.get('email')?.value || this.patient?.email || '',
        invoiceNumber,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((email) => !!email))
      .subscribe((email: string) => {
        const logItem = OrderDocument.emailLogItem(
          String(this.auth.currentUser?.initials || '').trim(),
          this.auth.currentUser._id,
          email
        );
        this.orderService.updateOrderById(order._id, {
          log: [logItem],
          dateUpdated: new Date().toISOString(),
        }).subscribe({
          next: () => {
            OrderDocument.openMailto(
              email,
              invoiceNumber,
              this.currentStore?.storeName
            );
            this.notificationService.show(
              'success',
              'Invoice emailed to customer',
              'OK'
            );
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not record email in the LOG',
              'OK'
            );
          },
        });
      });
  }

  returnOrder(inputObj): void {
    const original = inputObj.order;
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'RETURN FRAME?',
        order: original,
        mode: 'return',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.orderService
          .getPatientOrders(this.patientId, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ReturnJob.create$(
                original,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                  restockingFeeApplied:
                    dialogResult.applyRestockingFee !== false,
                },
                {
                  patientId: this.patientId,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials:
                    String(original?.initials || '').trim() ||
                    String(this.auth.currentUser?.initials || '').trim(),
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const storeCreditControl = this.patientForm.get('storeCredit');
              const storeCreditValue =
                FormatData.formatFloat(storeCreditControl.value) +
                FormatData.formatFloat(dialogResult.netCredit);
              storeCreditControl.setValue(storeCreditValue);
              this.notificationService.show(
                'success',
                'Return processed; original invoice unchanged',
                'OK'
              );
              if (created?._id) {
                this.router.navigate([
                  '/admin/patient',
                  this.patientId,
                  'order',
                  created._id,
                ]);
              }
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create return',
                'OK'
              );
            },
          });
      });
  }

  exchangeOrder(inputObj): void {
    const original = inputObj.order;
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'EXCHANGE FRAME?',
        order: original,
        mode: 'exchange',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.orderService
          .getPatientOrders(this.patientId, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ExchangeJob.create$(
                original,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                },
                {
                  patientId: this.patientId,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials:
                    String(original?.initials || '').trim() ||
                    String(this.auth.currentUser?.initials || '').trim(),
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const storeCreditControl = this.patientForm.get('storeCredit');
              const storeCreditValue =
                FormatData.formatFloat(storeCreditControl.value) +
                FormatData.formatFloat(dialogResult.netCredit);
              storeCreditControl.setValue(storeCreditValue);
              this.notificationService.show(
                'success',
                'Exchange job created; original invoice unchanged',
                'OK'
              );
              if (created?._id) {
                this.router.navigate([
                  '/admin/patient',
                  this.patientId,
                  'order',
                  created._id,
                ]);
              }
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create exchange job',
                'OK'
              );
            },
          });
      });
  }

  updatePatientCredit(storeCredit) {}

  openDialog(message): void {
    this.dialog.open(SaveComponent, {
      width: '250px',
      data: { message: message },
    });
  }

  blurPhone(prescriptionForm) {
    const phoneNumber = parsePhoneNumber(prescriptionForm.value, 'US');
    prescriptionForm.setValue(phoneNumber.formatNational());
  }

  phoneType(event, field) {
    const phoneNumber = parsePhoneNumber(
      this.patientForm.controls[field].value,
      'US'
    );
    this.patientForm.controls[field].setValue(phoneNumber.formatNational());
  }

  importCustomerImages() {
    this.router.navigate(['/admin/import-customer-images', this.patient._id]);
  }

  setNewPatientName(name) {
    if (name != undefined) {
      const lastFirst = [];
      let lastName = '';
      let firstName = '';
      if (name.includes(',')) {
        const pos = name.indexOf(',');
        lastName = name.slice(0, pos).trim();
        firstName = name.slice(pos + 1).trim();
      } else if (name.includes(' ')) {
        const pos = name.indexOf(' ');
        lastName = name.slice(0, pos).trim();
        firstName = name.slice(pos + 1).trim();
      }
      this.patientForm.get('firstName').setValue(firstName);
      this.patientForm.get('lastName').setValue(lastName);
    }
  }

  showPatientAlert(): void {
    this.dialog.open(OkCancelComponent, {
      width: '400px',
      height: '400px',
      closeOnNavigation: true,
      hasBackdrop: true,
      disableClose: true,
      id: this.patient.dns ? 'acknowledge-dns' : 'acknowledge',
      data: {
        message: this.patient.patientNotes,
        hideCancel: true,
        confirmText: 'ACKNOWLEDGE',
        postIt: true,
      },
    });
  }
}
